import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Download, ExternalLink, FileSignature, RefreshCcw } from 'lucide-react';
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { EmptyState, PageHeader, Table, type TableColumn } from '~/components/data';
import { fetchEsfList, fetchEsfMonthlyStats } from '~/lib/api/finance';
import { formatDate } from '~/lib/format';
import shared from './_auth._shared.module.css';
import styles from './_auth.finance.esf.index.module.css';

/**
 * S13-FE.5 — ESF list page.
 * FE-294 Pass 69: renamed `.tsx` → `.index.tsx` so list and `$id` detail
 * become sibling leaves вместо parent-without-Outlet (URL changed but list
 * перерисовывался поверх детали).
 */

export const Route = createFileRoute('/_auth/finance/esf/')({ component: FinanceEsfPage });

type Row = {
  id: string;
  kind: string;
  state: string;
  period: { date_from: string; date_to: string };
  created_at: string;
  onec_doc_ref?: string | null | undefined;
};

const STATE_LABELS: Record<string, () => string> = {
  draft: m.esf_state_draft,
  pending_approval: m.esf_state_pending_approval,
  approved: m.esf_state_approved,
  pushed_to_1c: m.esf_state_pushed_to_1c,
  rejected: m.esf_state_rejected,
  cancelled: m.esf_state_cancelled,
};

function getDefaultPeriod(): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  const fromDate = new Date(now);
  fromDate.setUTCFullYear(fromDate.getUTCFullYear() - 1);
  return { from: fromDate.toISOString().slice(0, 10), to };
}

function FinanceEsfPage(): JSX.Element {
  const query = useQuery({ queryKey: ['finance', 'esf'], queryFn: fetchEsfList });
  const rows: Row[] = (query.data?.items ?? []) as Row[];

  // S-FIX-2-FE.5.6: monthly stats BarChart (paired with BE.12.1).
  const period = useMemo(getDefaultPeriod, []);
  const monthlyQuery = useQuery({
    queryKey: ['finance', 'esf', 'monthly-stats', period.from, period.to],
    queryFn: () => fetchEsfMonthlyStats(period.from, period.to),
    staleTime: 60_000,
  });

  const columns: TableColumn<Row>[] = useMemo(
    () => [
      {
        id: 'id',
        header: m.esf_column_doc_number(),
        accessor: (r) => {
          // FE-101: business-like document number derived from period + short id.
          // Real `doc_no` field — BE-долг, пока compose из year + last 4 chars id.
          const year = r.period?.date_from?.slice(0, 4) ?? '2026';
          const seq = r.id.replace(/-/g, '').slice(-4).toUpperCase();
          const prefix = r.kind === 'esf' ? 'ЭСФ' : 'КПН';
          const docNo = `${prefix}-${year}-${seq}`;
          return (
            <Link to="/finance/esf/$id" params={{ id: r.id }} className={shared.accentLink}>
              <span className={shared.mono}>{docNo}</span>
            </Link>
          );
        },
        sticky: true,
      },
      {
        id: 'period',
        header: m.esf_column_period(),
        // FE-105: compact period — single day shows once, range shows «from — to».
        accessor: (r) => {
          const from = formatDate(r.period.date_from);
          const to = formatDate(r.period.date_to);
          return from === to ? from : `${from} — ${to}`;
        },
      },
      {
        // FE-103 honest-audit: StatusBadge-style pill для state.
        id: 'state',
        header: m.esf_column_state(),
        accessor: (r) => {
          const label = (STATE_LABELS[r.state] ?? (() => r.state))();
          const bgColor =
            r.state === 'approved' || r.state === 'pushed_to_1c'
              ? 'var(--ok-bg-15)'
              : r.state === 'rejected' || r.state === 'cancelled'
                ? 'var(--danger-bg-15)'
                : 'var(--warn-bg-15)';
          const fgColor =
            r.state === 'approved' || r.state === 'pushed_to_1c'
              ? 'var(--color-ok)'
              : r.state === 'rejected' || r.state === 'cancelled'
                ? 'var(--color-danger)'
                : 'var(--color-warn)';
          return (
            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 500,
                background: bgColor,
                color: fgColor,
              }}
            >
              {label}
            </span>
          );
        },
        width: '160px',
      },
      {
        id: 'onec',
        header: m.esf_column_onec_ref(),
        accessor: (r) =>
          r.onec_doc_ref ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontFamily: 'var(--font-mono)',
              }}
              title={`1С doc ref: ${r.onec_doc_ref}`}
            >
              <span>{r.onec_doc_ref}</span>
              <ExternalLink size={11} aria-hidden="true" />
            </span>
          ) : (
            '—'
          ),
        mono: true,
      },
      {
        // FE-104 honest-audit: row actions — Re-push / Download XML / View detail.
        id: 'actions',
        header: '',
        width: '140px',
        accessor: (r) => (
          <div style={{ display: 'flex', gap: 4 }}>
            <Link
              to="/finance/esf/$id"
              params={{ id: r.id }}
              className={shared.link}
              title="Открыть"
            >
              <ExternalLink size={14} />
            </Link>
            {r.state === 'rejected' || r.state === 'cancelled' ? (
              <button
                type="button"
                title="Повторная отправка"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: 2,
                }}
                aria-label="Повторная отправка"
              >
                <RefreshCcw size={14} />
              </button>
            ) : null}
            <button
              type="button"
              title="Скачать XML"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                padding: 2,
              }}
              aria-label="Скачать XML"
            >
              <Download size={14} />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  const chartData = monthlyQuery.data?.series ?? [];

  return (
    <div>
      <PageHeader title={m.phase2_finance_esf_title()} />
      {/* S-FIX-2-FE.5.6: monthly stats stacked bar chart */}
      {chartData.length > 0 && (
        <div className={styles.chartCard}>
          {/* FE-102 follow-up: chart readability fix — fontSize 12+, secondary text color
              (--color-text-secondary вместо subtle), height 240 для axes breathing room,
              X-axis labels angled (-35°) против overlap. */}
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 12, right: 12, left: 8, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                axisLine={{ stroke: 'var(--color-border)' }}
                angle={-35}
                textAnchor="end"
                height={50}
              />
              <YAxis
                tick={{ fontSize: 12, fill: 'var(--color-text-secondary)' }}
                axisLine={{ stroke: 'var(--color-border)' }}
                allowDecimals={false}
                width={40}
                label={{
                  value: 'шт.',
                  position: 'insideLeft',
                  offset: 12,
                  angle: -90,
                  style: { fontSize: 12, fill: 'var(--color-text-secondary)' },
                }}
              />
              {/* FE-102: tabular-nums tooltip + visible legend with все 3 labels. */}
              <Tooltip
                contentStyle={{
                  background: 'var(--color-bg-elevated)',
                  border: '1px solid var(--color-border)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: '12px' }}
                iconType="rect"
                verticalAlign="bottom"
                height={28}
              />
              {/* FE-265 Pass 28: localized legend names + rounded top bar. */}
              <Bar
                dataKey="pushed_count"
                stackId="s"
                fill="var(--chart-positive)"
                name={m.esf_chart_legend_pushed()}
              />
              <Bar
                dataKey="approved_count"
                stackId="s"
                fill="var(--chart-primary)"
                name={m.esf_chart_legend_approved()}
              />
              <Bar
                dataKey="draft_count"
                stackId="s"
                fill="var(--chart-warning)"
                name={m.esf_chart_legend_draft()}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {rows.length === 0 && !query.isLoading ? (
        <EmptyState title={m.esf_empty_title()} icon={<FileSignature size={32} />} />
      ) : (
        <Table<Row>
          data={rows}
          columns={columns}
          rowKey={(r) => r.id}
          ariaLabel={m.phase2_finance_esf_title()}
          loading={query.isLoading}
          emptyTitle={m.esf_empty_title()}
        />
      )}
    </div>
  );
}
